import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { 
  Link2, 
  AlertCircle, 
  CheckCircle2,
  Trash2,
  RefreshCw,
  Copy,
  AlertTriangle,
  Settings
} from "lucide-react";
import { SiFacebook, SiInstagram, SiTiktok } from "react-icons/si";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import PartnerLayout from "./PartnerLayout";

interface SocialConnection {
  id: string;
  platform: 'facebook' | 'instagram' | 'tiktok';
  platformAccountId: string;
  platformAccountName: string | null;
  platformAccountHandle: string | null;
  platformProfilePictureUrl: string | null;
  isActive: boolean;
  lastUsedAt: string | null;
  lastError: string | null;
  accessTokenExpiresAt: string | null;
  createdAt: string;
}

interface OAuthStatus {
  configured: boolean;
  redirectUri: string;
}

const platformConfig = {
  facebook: {
    name: 'Facebook',
    icon: SiFacebook,
    color: 'text-[#1877F2]',
    bgColor: 'bg-[#1877F2]',
    description: 'Post to your Facebook Page',
  },
  instagram: {
    name: 'Instagram',
    icon: SiInstagram,
    color: 'text-[#E4405F]',
    bgColor: 'bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF]',
    description: 'Share to Instagram Business',
  },
  tiktok: {
    name: 'TikTok',
    icon: SiTiktok,
    color: 'text-black dark:text-white',
    bgColor: 'bg-black',
    description: 'Coming soon',
  },
};

const TOKEN_EXPIRY_WARNING_DAYS = 7;

function isTokenExpiringSoon(expiresAt: string | null): boolean {
  if (!expiresAt) return false;
  const expiry = new Date(expiresAt);
  const warningDate = new Date();
  warningDate.setDate(warningDate.getDate() + TOKEN_EXPIRY_WARNING_DAYS);
  return expiry <= warningDate;
}

function isTokenExpired(expiresAt: string | null): boolean {
  if (!expiresAt) return false;
  return new Date(expiresAt) <= new Date();
}

function needsReconnect(connection: SocialConnection): boolean {
  return !!connection.lastError || isTokenExpiringSoon(connection.accessTokenExpiresAt);
}

export default function PartnerSettings() {
  const { toast } = useToast();
  const restaurantId = localStorage.getItem('partnerRestaurantId');
  const userId = localStorage.getItem('partnerUserId');
  const [connectingPlatform, setConnectingPlatform] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const error = params.get('error');
    const message = params.get('message');
    const pages = params.get('pages');
    const instagram = params.get('instagram');

    if (success === 'connected') {
      let description = "Your social media account is now connected.";
      if (pages) {
        const instagramPart = instagram && parseInt(instagram) > 0 
          ? ` and ${instagram} Instagram account(s)` 
          : '';
        description = `Connected ${pages} Facebook page(s)${instagramPart} to your account.`;
      }
      toast({
        title: "Connected successfully!",
        description,
      });
      window.history.replaceState({}, '', window.location.pathname);
      queryClient.invalidateQueries({ queryKey: ['/api/partner/social/connections'] });
    } else if (error) {
      toast({
        title: "Connection failed",
        description: message || `Error: ${error}`,
        variant: "destructive",
      });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [toast]);

  const { data: oauthStatus } = useQuery<OAuthStatus>({
    queryKey: ['/api/partner/social/oauth/status'],
  });

  const { data: connections, isLoading } = useQuery<SocialConnection[]>({
    queryKey: ['/api/partner/social/connections', restaurantId],
    queryFn: async () => {
      const res = await apiRequest('GET', `/api/partner/social/connections?restaurantId=${restaurantId}`);
      return res.json();
    },
    enabled: !!restaurantId,
  });

  const disconnectMutation = useMutation({
    mutationFn: async ({ id }: { id: string; platform: string }) => {
      const res = await apiRequest('DELETE', `/api/partner/social/connections/${id}`);
      return res.json();
    },
    onSuccess: (_, variables) => {
      const { platform } = variables;
      // Platforms to remove: the disconnected one plus instagram if facebook was removed
      const platformsToRemove = new Set<string>([platform, ...(platform === 'facebook' ? ['instagram'] : [])]);
      // Immediately wipe from cache so the UI updates before the refetch completes
      queryClient.setQueryData<SocialConnection[]>(
        ['/api/partner/social/connections', restaurantId],
        (old) => (old ? old.filter((c) => !platformsToRemove.has(c.platform)) : old)
      );
      toast({ title: "Disconnected successfully" });
      queryClient.invalidateQueries({ queryKey: ['/api/partner/social/connections', restaurantId] });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to disconnect",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleConnectFacebook = async () => {
    if (!restaurantId) {
      toast({
        title: "No restaurant selected",
        description: "Please select a restaurant first",
        variant: "destructive",
      });
      return;
    }

    setConnectingPlatform('facebook');
    
    try {
      const response = await apiRequest(
        'GET',
        `/api/partner/social/oauth/facebook/start?restaurantId=${restaurantId}&userId=${userId}`
      );
      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else if (data.error) {
        toast({
          title: "Connection failed",
          description: data.error,
          variant: "destructive",
        });
        setConnectingPlatform(null);
      }
    } catch (error: any) {
      toast({
        title: "Connection failed",
        description: error.message,
        variant: "destructive",
      });
      setConnectingPlatform(null);
    }
  };

  const getConnectionForPlatform = (platform: string) => {
    return connections?.find(c => c.platform === platform && c.isActive);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleCopyRedirectUri = () => {
    if (oauthStatus?.redirectUri) {
      navigator.clipboard.writeText(oauthStatus.redirectUri);
      toast({ title: "Copied to clipboard" });
    }
  };

  return (
    <PartnerLayout>
      <div className="space-y-6 max-w-4xl">
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-settings-title">Settings</h1>
          <p className="text-muted-foreground">Manage your restaurant's integrations and preferences</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Social Media Connections
            </CardTitle>
            <CardDescription>
              Connect your social media accounts to cross-post your content automatically
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {oauthStatus && !oauthStatus.configured && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Meta App not configured</AlertTitle>
                <AlertDescription>
                  Facebook and Instagram connections require a Meta App ID and Secret. 
                  Set the <code className="text-xs font-mono bg-muted px-1 py-0.5 rounded">META_APP_ID</code> and <code className="text-xs font-mono bg-muted px-1 py-0.5 rounded">META_APP_SECRET</code> environment 
                  variables to enable connections.
                </AlertDescription>
              </Alert>
            )}

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Connect Facebook to unlock Instagram</AlertTitle>
              <AlertDescription>
                Instagram Business accounts are connected through Facebook Pages. 
                Connect your Facebook account first, and any linked Instagram accounts will be automatically added.
              </AlertDescription>
            </Alert>

            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-4 p-4 border rounded-lg">
                    <Skeleton className="h-12 w-12 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                    <Skeleton className="h-9 w-24" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {(['facebook', 'instagram', 'tiktok'] as const).map((platform) => {
                  const config = platformConfig[platform];
                  const connection = getConnectionForPlatform(platform);
                  const Icon = config.icon;
                  const showReconnect = connection && needsReconnect(connection) && (platform === 'facebook' || platform === 'instagram');
                  const expired = connection && isTokenExpired(connection.accessTokenExpiresAt);

                  return (
                    <div 
                      key={platform} 
                      className="flex items-center gap-4 p-4 border rounded-lg"
                      data-testid={`social-connection-${platform}`}
                    >
                      <div className={`p-3 rounded-full ${config.bgColor} text-white`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{config.name}</span>
                          {connection ? (
                            expired ? (
                              <Badge variant="destructive" className="gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                Expired
                              </Badge>
                            ) : showReconnect ? (
                              <Badge variant="secondary" className="gap-1 text-orange-600 dark:text-orange-400">
                                <AlertTriangle className="h-3 w-3" />
                                Needs reconnection
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="gap-1">
                                <CheckCircle2 className="h-3 w-3" />
                                Connected
                              </Badge>
                            )
                          ) : (
                            <Badge variant="outline">Not connected</Badge>
                          )}
                        </div>
                        
                        {connection ? (
                          <div className="text-sm text-muted-foreground mt-1">
                            <span className="font-medium">
                              {connection.platformAccountHandle || connection.platformAccountName}
                            </span>
                            {connection.lastUsedAt && (
                              <span className="ml-2">
                                Last used: {formatDate(connection.lastUsedAt)}
                              </span>
                            )}
                            {connection.lastError && (
                              <div className="text-destructive mt-1 flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />
                                {connection.lastError}
                              </div>
                            )}
                            {expired && (
                              <div className="text-destructive mt-1 flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />
                                Token expired. Please reconnect your account.
                              </div>
                            )}
                            {!expired && isTokenExpiringSoon(connection.accessTokenExpiresAt) && (
                              <div className="text-orange-600 dark:text-orange-400 mt-1 flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                Token expires {formatDate(connection.accessTokenExpiresAt!)}
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">{config.description}</p>
                        )}
                      </div>

                      {connection ? (
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {showReconnect ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleConnectFacebook}
                              disabled={connectingPlatform === 'facebook' || (oauthStatus && !oauthStatus.configured)}
                              data-testid={`button-reconnect-${platform}`}
                            >
                              <RefreshCw className={`h-4 w-4 mr-2 ${connectingPlatform === 'facebook' ? 'animate-spin' : ''}`} />
                              Reconnect
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => disconnectMutation.mutate({ id: connection.id, platform: connection.platform })}
                              disabled={disconnectMutation.isPending}
                              data-testid={`button-disconnect-${platform}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      ) : platform === 'tiktok' ? (
                        <Button variant="outline" disabled>
                          Coming Soon
                        </Button>
                      ) : platform === 'instagram' ? (
                        <Button 
                          variant="outline" 
                          disabled={!getConnectionForPlatform('facebook')}
                          onClick={handleConnectFacebook}
                          data-testid={`button-connect-${platform}`}
                        >
                          <Link2 className="h-4 w-4 mr-2" />
                          {!getConnectionForPlatform('facebook') ? 'Connect Facebook first' : 'Connect via Facebook'}
                        </Button>
                      ) : (
                        <Button 
                          onClick={handleConnectFacebook}
                          disabled={connectingPlatform === 'facebook' || (oauthStatus && !oauthStatus.configured)}
                          data-testid={`button-connect-${platform}`}
                        >
                          {connectingPlatform === 'facebook' ? (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              Connecting...
                            </>
                          ) : (
                            <>
                              <Link2 className="h-4 w-4 mr-2" />
                              Connect
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <Separator />

            <div className="text-sm text-muted-foreground space-y-2">
              <p className="font-medium">How cross-posting works:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Create a post in the Partner Portal as usual</li>
                <li>Select which platforms to cross-post to</li>
                <li>Content is automatically formatted for each platform</li>
                <li>Track performance across all platforms in Analytics</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Platform Requirements</CardTitle>
            <CardDescription>
              What you need to connect each platform
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2 p-4 border rounded-lg">
                <div className="flex items-center gap-2">
                  <SiFacebook className="h-5 w-5 text-[#1877F2]" />
                  <span className="font-medium">Facebook</span>
                </div>
                <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                  <li>Facebook Business Page</li>
                  <li>Admin access to the page</li>
                  <li>Page must be published (not draft)</li>
                </ul>
              </div>
              
              <div className="space-y-2 p-4 border rounded-lg">
                <div className="flex items-center gap-2">
                  <SiInstagram className="h-5 w-5 text-[#E4405F]" />
                  <span className="font-medium">Instagram</span>
                </div>
                <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                  <li>Instagram Business or Creator account</li>
                  <li>Linked to a Facebook Page</li>
                  <li>Posts must include an image (JPEG)</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {oauthStatus && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Meta Developer Setup
              </CardTitle>
              <CardDescription>
                Configuration details for your Meta (Facebook) Developer App
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">OAuth Redirect URI</label>
                <p className="text-xs text-muted-foreground">
                  Add this URL as a Valid OAuth Redirect URI in your Meta App settings under Facebook Login
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={oauthStatus.redirectUri}
                    className="font-mono text-xs"
                    data-testid="input-redirect-uri"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleCopyRedirectUri}
                    data-testid="button-copy-redirect-uri"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <p className="text-sm font-medium">Required Permissions (Scopes)</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    'pages_show_list',
                    'pages_read_engagement',
                    'pages_manage_posts',
                    'instagram_basic',
                    'instagram_content_publish',
                  ].map((scope) => (
                    <Badge key={scope} variant="outline" className="font-mono text-xs">
                      {scope}
                    </Badge>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  These permissions must be approved through Meta App Review before non-developer users can connect.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PartnerLayout>
  );
}
